import { ErrorBoundary } from "@bindery/ui/components/error-boundary";
import { Suspense, type ComponentType } from "react";
import { useDesign } from "./design-context";
import { DesignSkeleton } from "./skeleton";

interface DesignShellProps<P extends object> {
  componentMap: Record<string, ComponentType<P>>;
  componentProps: P;
}

function DesignErrorFallback({ variantName }: { variantName: string }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: "#f5f5f5",
      }}
    >
      <div
        style={{
          maxWidth: "500px",
          textAlign: "center",
          background: "white",
          padding: "2rem",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.5rem",
            color: "#333",
          }}
        >
          Design Variant Error
        </h2>
        <p
          style={{
            fontSize: "1rem",
            color: "#666",
            marginBottom: "1rem",
          }}
        >
          The <strong>{variantName}</strong> variant failed to render.
        </p>
        <p
          style={{
            fontSize: "0.875rem",
            color: "#999",
            marginBottom: "1.5rem",
          }}
        >
          Try switching to a different design variant using the design picker.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.75rem 1.5rem",
            background: "#333",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "1rem",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}

export function DesignShell<P extends object>({
  componentMap,
  componentProps,
}: DesignShellProps<P>) {
  const { activeSlug } = useDesign();
  const ActiveComponent = componentMap[activeSlug];

  if (!ActiveComponent) {
    return <DesignSkeleton />;
  }

  return (
    <ErrorBoundary
      onError={(error, info) => {
        console.error(`[design] ${activeSlug} failed:`, error);
        console.error("Error details:", info);
      }}
      fallback={() => <DesignErrorFallback variantName={activeSlug} />}
    >
      <Suspense fallback={<DesignSkeleton />}>
        <ActiveComponent {...componentProps} />
      </Suspense>
    </ErrorBoundary>
  );
}
